import { type Link } from "../../../../components/ExpandableItems/types";
import { type PackName } from "../../../../interfaces";
import { type SurveyQuestion as SurveyContentViewQuestion } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/types";
import { type SurveyOverview } from "./models";
import { type PaginationAndSortingOptions } from "features/Library/Common/models";
import { type SortingDirection } from "enums";

export type SurveyOverviewState = {
  surveys: SurveyOverview[];
  isLoading: boolean;
  totalCount: number;
  error?: Error;
  selectedItems: number[];
};

export type Survey = {
  id?: number;
  isDraft: boolean;
  isPurchased: boolean;
  hasBeenPublished?: boolean;
  title: string;
  description: string;
  packs: PackName[];
  ownerId: number;
  dateCreated?: string;
  dateModified?: string;
  labels: string[];
  softwareApplications: string[];
  endNoteText: string;
  endNoteTextEnabled: boolean;
  flows: FlowName[];
  pagination: number;
  sortingColumnName: string;
  sortingDirection: SortingDirection;
} & PaginationAndSortingOptions;

export type SurveyView = {
  id: number;
  isDraft: boolean;
  isPurchased: boolean;
  hasBeenPublished: boolean;
  title: string;
  description: string;
  dateCreated?: string;
  dateModified?: string;
  endNoteText: string;
  endNoteTextEnabled: boolean;
  ownerId: number;
  flows: FlowName[];
};

export type SettingsView = {
  labels: string[];
  softwareApplications: string[];
  flows: FlowName[];
  packs: Link[];
};

export type SurveyContentView = {
  questions: SurveyContentViewQuestion<any>[];
};

export type SurveyDetailsState = {
  title: string;
  isDraft: boolean;
  survey: SurveyView;
  content: SurveyContentView;
  settings: SettingsView;
  isLoading: boolean;
  isSurveyInfoLoaded: boolean;
  error?: Error;
  isContentLoaded: boolean;
};

export type Dependencies = {
  flowsCount: number;
  assignmentsCount: number;
};

interface FlowName {
  id: number;
  title: string;
}

export interface SurveyTags {
  labels: string[];
  softwareApplications: string[];
}

// Survey Performance
export interface SurveyQuestionAnsweredUser {
  AccountId: number;
  AccountName: string;
  AnswerDate: string;
  /**
   * If IsOtherQuestion, contains their string response
   */
  AnswerResult: string;
  AnswerText: string;
  Department: string;
  Email: string;
  FirstName: string;
  LastName: string;
  Title: string;
  UserId: number;
}

export type SurveyPerformanceAnswer = {
  AnswerId: string;
  AnswerText: string;
  AnsweredPeople: number;
  AnsweredPercent: number;
  QuestionId: string;
  QuestionVersion: number;
  SurveyId: number;
  Users: SurveyQuestionAnsweredUser[];
};

export interface SurveyQuestionAnswer {
  AnswerId: string;
  AnswerText: string;
  AnsweredPeople: number;
}

export enum SurveyQuestionType {
  MultipleSelect = 1,
  SingleSelect = 2,
  FreeText = 3,
  RatingScale = 4,
}

export type SurveyQuestion = {
  Answers: SurveyQuestionAnswer[];
  CompletionRate: number;
  Completions: number;
  HighestRankLabel: string;
  IsQuestionRequired: boolean;
  LowestRankLabel: string;
  QuestionId: string;
  QuestionText: string;
  QuestionType: SurveyQuestionType;
  QuestionVersion: number;
  Skipped: string;
  SurveyId: number;
};

export type SurveyAnswer = {
  Answers: SurveyQuestionAnswer[];
  CompletionRate: number;
  Completions: number;
  HighestRankLabel: string;
  IsQuestionRequired: boolean;
  LowestRankLabel: string;
  QuestionId: string;
  QuestionText: string;
  QuestionType: number;
  QuestionVersion: number;
  Skipped: string;
  SurveyId: number;
};

export type SurveyPeopleDetails = {
  AccountId: number;
  CompletionDate: string;
  Department: string;
  Email: string;
  FirstName: string;
  LastName: string;
  QuestionsCompleted: number;
  StartDate: string;
  TimesTaken: number;
  UserId: number;
  UserTitle: string;
};
